import { SupportAlert } from '@/components/support-alert';
import { Ticket } from '@/ticket/api/types';
import { useCloseTicket } from '@/ticket/hooks/use-close-ticket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  reason: z.string().nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  ticket: Ticket;
  close: () => void;
}

export function TicketCloseDialog(props: Props) {
  const { handleSubmit, reset, register } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: null,
    },
  });

  const closeTicketMutation = useCloseTicket(props.ticket.key, {
    onSuccess: (_data) => {
      reset();
      props.close();
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    closeTicketMutation.mutate({
      closeReason: data.reason ?? undefined,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={props.close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <DialogTitle>Close ticket</DialogTitle>
      <DialogContent>
        {closeTicketMutation.isError && (
          <SupportAlert message={'Something went wrong.'} />
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Reason (optional)"
          placeholder="(Optional) Provide the reason for closing this ticket"
          fullWidth
          multiline
          rows={4}
          {...register('reason')}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            closeTicketMutation.reset();
            props.close();
          }}
        >
          Cancel
        </Button>
        <Button type="submit" color="primary" variant="contained">
          Close ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
}
